/* eslint-disable @typescript-eslint/no-explicit-any */
// Innobo Hub UI kit v1. Las pantallas nuevas (y las que vayamos migrando) usan esto en vez de reactstrap.
// El estilo vive en ihub.scss bajo .ihub: las pantallas viejas no cambian.
// Empezó con Notifications; la idea es pasar lista por lista.
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Select from 'react-select';
import moment from 'moment';
// @ts-ignore: react-flatpickr no trae tipos en esta versión
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import './ihub.scss';
import './ihub-calendar.scss';

// Fuentes del manual: Roboto (textos) y Barlow como respaldo web de Bahnschrift (títulos; Bahnschrift solo existe en Windows).
// Se cargan una sola vez, sin depender del orden del CSS del build.
if (typeof document !== 'undefined' && !document.getElementById('ihub-font')) {
  const l = document.createElement('link');
  l.id = 'ihub-font';
  l.rel = 'stylesheet';
  l.href = 'https://fonts.googleapis.com/css2?family=Barlow:wght@600;700;800&family=Roboto:wght@400;500;700;900&display=swap';
  document.head.appendChild(l);
}

const cx = (...c: any[]) => c.filter(Boolean).join(' ');
const F = 'YYYY-MM-DD';

/* ---------- Tema ----------
   theme="innobo": colores de Innobo Hub (paneles de admin).
   theme="brand": toma brand.theme del login (primaryColor / secondaryColor) y deriva el resto
   (fondos suaves, anillo de foco, color de texto encima del botón según el contraste).
   Los colores de estado (verde Sent, rojo Error) no cambian con el cliente. */
const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const aRgb = (hex: string) => {
  let h = hex.slice(1);
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
};
const aHex = (c: number[]) => `#${c.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('')}`;
// t = cuánto de b
const mezclar = (a: string, b: string, t: number) => {
  const x = aRgb(a);
  const y = aRgb(b);
  return aHex(x.map((v, i) => v * (1 - t) + y[i] * t));
};
const luz = (hex: string) => {
  const [r, g, b] = aRgb(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
};
const rgba = (hex: string, a: number) => {
  const [r, g, b] = aRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};
const VARS_TEMA = [
  '--ih-primary',
  '--ih-on-primary',
  '--ih-primary-ink',
  '--ih-primary-shadow',
  '--ih-tint',
  '--ih-tint-strong',
  '--ih-ring',
  '--ih-navy',
  '--ih-accent',
  '--ih-eyebrow',
];
export const aplicarTema = (modo: string) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  VARS_TEMA.forEach((v) => root.style.removeProperty(v));
  if (modo !== 'brand') return;
  let theme: any = {};
  try {
    theme = JSON.parse(localStorage.getItem('brand') || '{}')?.theme || {};
  } catch (e) {
    theme = {};
  }
  const p = HEX.test(theme.primaryColor || '') ? theme.primaryColor : null;
  if (!p) return;
  const s = HEX.test(theme.secondaryColor || '') ? theme.secondaryColor : p;
  const poner = (k: string, v: string) => root.style.setProperty(k, v);
  poner('--ih-primary', p);
  poner('--ih-on-primary', luz(p) > 0.62 ? '#14151f' : '#ffffff');
  poner('--ih-primary-ink', luz(p) > 0.55 ? mezclar(p, '#000000', 0.45) : p);
  poner('--ih-primary-shadow', `0 7px 16px ${rgba(p, 0.22)}`);
  poner('--ih-tint', mezclar(p, '#ffffff', 0.92));
  poner('--ih-tint-strong', mezclar(p, '#ffffff', 0.82));
  poner('--ih-ring', `0 0 0 4px ${rgba(p, 0.14)}`);
  poner('--ih-navy', mezclar(p, '#14151f', 0.78));
  poner('--ih-accent', s);
  poner('--ih-eyebrow', luz(s) > 0.55 ? mezclar(s, '#000000', 0.45) : s);
};

/* ---------- Página ---------- */
export const Page = ({ eyebrow, title, subtitle, actions, children, theme = 'innobo' }: any) => {
  useLayoutEffect(() => {
    aplicarTema(theme);
  }, [theme]);
  return (
    <div className="page-content ihub">
      <div className="ihub-shell">
        <header className="ihub-head">
          <div>
            {eyebrow ? <div className="ihub-eyebrow">{eyebrow}</div> : null}
            <h1 className="ihub-title">{title}</h1>
            {subtitle ? <p className="ihub-subtitle">{subtitle}</p> : null}
          </div>
          {actions ? <div className="ihub-actions">{actions}</div> : null}
        </header>
        {children}
      </div>
    </div>
  );
};

export const Card = ({ accent, flush, className, children }: any) => (
  <section className={cx('ihub-card', accent && 'ihub-card--accent', flush && 'ihub-card--flush', className)}>{children}</section>
);

/* ---------- Botones y badges ---------- */
export const Button = ({ variant = 'primary', size, icon, children, className, type = 'button', ...rest }: any) => (
  <button
    type={type}
    className={cx('ihub-btn', `ihub-btn--${variant}`, size && `ihub-btn--${size}`, !children && 'ihub-btn--icon', className)}
    {...rest}
  >
    {icon ? <i className={cx('bx', icon)} /> : null}
    {children ? <span>{children}</span> : null}
  </button>
);

export const Badge = ({ tone = 'neutral', dot, children }: any) => (
  <span className={cx('ihub-badge', `ihub-badge--${tone}`)}>
    {dot ? <span className="ihub-badge__dot" /> : null}
    {children}
  </span>
);

export const Alert = ({ tone = 'info', title, children }: any) => (
  <div className={cx('ihub-alert', `ihub-alert--${tone}`)} role="alert">
    {title ? <div className="ihub-alert__title">{title}</div> : null}
    <div className="ihub-alert__body">{children}</div>
  </div>
);

export const EmptyState = ({ icon = 'bx-search-alt', title = 'Nothing here yet', text }: any) => (
  <div className="ihub-empty">
    <span className="ihub-empty__icon">
      <i className={cx('bx', icon)} />
    </span>
    <div className="ihub-empty__title">{title}</div>
    {text ? <p className="ihub-empty__text">{text}</p> : null}
  </div>
);

/* ---------- Métricas ---------- */
export const Stat = ({ label, value, tone = 'neutral', icon, active, onClick, loading }: any) => (
  <button type="button" className={cx('ihub-stat', `ihub-stat--${tone}`, active && 'is-on')} onClick={onClick} disabled={!onClick}>
    <span className="ihub-stat__label">
      {icon ? <i className={cx('bx', icon)} /> : null}
      {label}
    </span>
    <span className="ihub-stat__value">
      {loading ? <span className="ihub-skel" style={{ display: 'inline-block', width: 64, height: 26 }} /> : Number(value || 0).toLocaleString()}
    </span>
  </button>
);

/* ---------- Campos ---------- */
export const Field = ({ label, hint, className, children }: any) => (
  <div className={cx('ihub-field', className)}>
    {label ? <label className="ihub-label">{label}</label> : null}
    {children}
    {hint ? <span className="ihub-hint">{hint}</span> : null}
  </div>
);

export const TextInput = ({ icon, className, ...rest }: any) =>
  icon ? (
    <div className="ihub-inputwrap">
      <i className={cx('bx', icon)} />
      <input className={cx('ihub-input', className)} {...rest} />
    </div>
  ) : (
    <input className={cx('ihub-input', className)} {...rest} />
  );

export const Segmented = ({ options, value, onChange, size }: any) => (
  <div className={cx('ihub-seg', size && `ihub-seg--${size}`)} role="radiogroup">
    {options.map((o: any) => (
      <button
        key={String(o.value)}
        type="button"
        role="radio"
        aria-checked={o.value === value}
        className={cx(o.value === value && 'is-on')}
        onClick={() => onChange(o.value)}
      >
        {o.label}
      </button>
    ))}
  </div>
);

// react-select con el mismo look de los inputs. El menú va a <body> para que ninguna tarjeta lo corte.
const selectStyles: any = {
  control: (b: any, s: any) => ({
    ...b,
    minHeight: 44,
    borderRadius: 10,
    borderColor: s.isFocused ? 'var(--ih-primary)' : '#DADAF0',
    background: s.isFocused ? '#FFF' : '#FCFCFF',
    boxShadow: s.isFocused ? 'var(--ih-ring)' : 'none',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': { borderColor: s.isFocused ? 'var(--ih-primary)' : '#C4C4E6' },
  }),
  valueContainer: (b: any) => ({ ...b, padding: '0 12px' }),
  placeholder: (b: any) => ({ ...b, color: '#8B8BB3', fontWeight: 500 }),
  singleValue: (b: any) => ({ ...b, color: 'var(--ih-navy)' }),
  input: (b: any) => ({ ...b, color: 'var(--ih-navy)' }),
  indicatorSeparator: () => ({ display: 'none' }),
  clearIndicator: (b: any) => ({ ...b, color: '#8B8BB3', padding: 6, ':hover': { color: '#E02401' } }),
  dropdownIndicator: (b: any, s: any) => ({
    ...b,
    color: '#4E4E83',
    transition: 'transform .15s',
    transform: s.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
  }),
  menuPortal: (b: any) => ({ ...b, zIndex: 1060 }),
  menu: (b: any) => ({
    ...b,
    marginTop: 6,
    borderRadius: 12,
    border: '1px solid #DADAF0',
    boxShadow: '0 18px 50px rgba(26,26,91,.16)',
    overflow: 'hidden',
    fontFamily: "Roboto, system-ui, -apple-system, 'Segoe UI', Arial, sans-serif",
  }),
  menuList: (b: any) => ({ ...b, padding: 6 }),
  option: (b: any, s: any) => ({
    ...b,
    borderRadius: 8,
    fontSize: 13.5,
    fontWeight: s.isSelected ? 700 : 500,
    color: s.isSelected ? 'var(--ih-on-primary)' : 'var(--ih-navy)',
    background: s.isSelected ? 'var(--ih-primary)' : s.isFocused ? 'var(--ih-tint)' : 'transparent',
    cursor: 'pointer',
    ':active': { background: s.isSelected ? 'var(--ih-primary)' : 'var(--ih-tint-strong)' },
  }),
  noOptionsMessage: (b: any) => ({ ...b, fontSize: 13, color: '#4E4E83' }),
};

export const SelectField = ({ options = [], value, onChange, placeholder = 'All', isClearable = true, isSearchable = true }: any) => {
  const actual = options.find((o: any) => String(o.value) === String(value)) || null;
  return (
    <Select
      options={options}
      value={actual}
      onChange={(o: any) => onChange(o ? o.value : '')}
      placeholder={placeholder}
      isClearable={isClearable}
      isSearchable={isSearchable}
      styles={selectStyles}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
    />
  );
};

/* ---------- Rango de fechas ----------
   Rangos predefinidos como en Magaya (los clientes ya los conocen) + calendario flatpickr para Custom.
   Semanas de domingo a sábado, igual que Magaya. */
const hoy = () => moment().startOf('day');
const PRESETS: any[] = [
  { key: 'today', label: 'Today', range: () => [hoy(), hoy()] },
  { key: 'yesterday', label: 'Yesterday', range: () => [hoy().subtract(1, 'day'), hoy().subtract(1, 'day')] },
  { key: 'last7', label: 'Last 7 days', range: () => [hoy().subtract(6, 'days'), hoy()] },
  { key: 'last30', label: 'Last 30 days', range: () => [hoy().subtract(29, 'days'), hoy()] },
  { key: 'thisWeek', label: 'This week', range: () => [hoy().startOf('week'), hoy().endOf('week')] },
  { key: 'thisWeekToDate', label: 'This week to date', range: () => [hoy().startOf('week'), hoy()] },
  { key: 'lastWeek', label: 'Last week', range: () => [hoy().subtract(1, 'week').startOf('week'), hoy().subtract(1, 'week').endOf('week')] },
  { key: 'lastWeekToDate', label: 'Last week to date', range: () => [hoy().subtract(1, 'week').startOf('week'), hoy()] },
  { key: 'thisMonth', label: 'This month', range: () => [hoy().startOf('month'), hoy().endOf('month')] },
  { key: 'thisMonthToDate', label: 'This month to date', range: () => [hoy().startOf('month'), hoy()] },
  { key: 'lastMonth', label: 'Last month', range: () => [hoy().subtract(1, 'month').startOf('month'), hoy().subtract(1, 'month').endOf('month')] },
  { key: 'lastMonthToDate', label: 'Last month to date', range: () => [hoy().subtract(1, 'month').startOf('month'), hoy()] },
  { key: 'last3Months', label: 'Last three months', range: () => [hoy().subtract(3, 'months'), hoy()] },
  { key: 'last6Months', label: 'Last six months', range: () => [hoy().subtract(6, 'months'), hoy()] },
  { key: 'thisYear', label: 'This year', range: () => [hoy().startOf('year'), hoy().endOf('year')] },
  { key: 'thisYearToDate', label: 'This year to date', range: () => [hoy().startOf('year'), hoy()] },
  { key: 'lastYear', label: 'Last year', range: () => [hoy().subtract(1, 'year').startOf('year'), hoy().subtract(1, 'year').endOf('year')] },
  { key: 'lastYearToDate', label: 'Last year to date', range: () => [hoy().subtract(1, 'year').startOf('year'), hoy()] },
  { key: 'nextWeek', label: 'Next week', future: true, range: () => [hoy().add(1, 'week').startOf('week'), hoy().add(1, 'week').endOf('week')] },
  { key: 'nextMonth', label: 'Next month', future: true, range: () => [hoy().add(1, 'month').startOf('month'), hoy().add(1, 'month').endOf('month')] },
];
const coincide = (p: any, from: string, to: string) => {
  const [a, b] = p.range();
  return a.format(F) === from && b.format(F) === to;
};
const detectar = (lista: any[], from: string, to: string) => {
  const p = lista.find((x) => coincide(x, from, to));
  return p ? p.key : 'custom';
};

// Clase del tema + botón "Today" abajo para volver al mes actual de un clic.
const prepararCalendario = (fp: any) => {
  const c = fp.calendarContainer;
  c.classList.add('ihub-fp');
  if (c.querySelector('.ihub-fp__foot')) return;
  const pie = document.createElement('div');
  pie.className = 'ihub-fp__foot';
  const b = document.createElement('button');
  b.type = 'button';
  b.textContent = 'Today';
  b.addEventListener('click', () => fp.jumpToDate(new Date()));
  pie.appendChild(b);
  c.appendChild(pie);
};

// Constante fuera del componente: react-flatpickr re-aplica las opciones que cambian de referencia.
const FP_OPCIONES = {
  mode: 'range',
  dateFormat: 'Y-m-d',
  altInput: true,
  altFormat: 'M j, Y',
  altInputClass: 'ihub-input ihub-input--date',
  locale: { rangeSeparator: '  →  ', firstDayOfWeek: 0 },
};

// presets: lista de keys para limitar las opciones. allowFuture: incluye Next week / Next month (útil para ETAs).
export const DateRange = ({ from, to, onChange, presets, allowFuture = false }: any) => {
  const lista = PRESETS.filter((p) => (presets ? presets.includes(p.key) : allowFuture || !p.future));
  const [preset, setPreset] = useState<string>(() => detectar(lista, from, to));
  const fpRef = useRef<any>(null);
  // Mismo array mientras no cambien las fechas: si no, flatpickr resetea el rango a mitad de selección.
  const valor = useMemo(() => [from, to].filter(Boolean), [from, to]);

  // Si las fechas cambian desde afuera (Clear filters) o desde el calendario, re-detecta el rango.
  useEffect(() => {
    setPreset((actual) => {
      const p = PRESETS.find((x) => x.key === actual);
      if (p && coincide(p, from, to)) return actual;
      return detectar(lista, from, to);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  const elegir = (key: string) => {
    if (key === 'custom') {
      setPreset('custom');
      setTimeout(() => fpRef.current && fpRef.current.flatpickr && fpRef.current.flatpickr.open(), 0);
      return;
    }
    const p = PRESETS.find((x) => x.key === key);
    if (!p) return;
    const [a, b] = p.range();
    setPreset(key);
    onChange(a.format(F), b.format(F));
  };

  return (
    <div className="ihub-daterange">
      <SelectField
        options={[...lista.map((p) => ({ value: p.key, label: p.label })), { value: 'custom', label: 'Custom' }]}
        value={preset}
        onChange={elegir}
        isClearable={false}
        isSearchable={false}
        placeholder="Range"
      />
      <Flatpickr
        ref={fpRef}
        value={valor}
        options={FP_OPCIONES}
        onReady={(_d: any, _s: any, fp: any) => prepararCalendario(fp)}
        onClose={(dates: any[], _s: any, fp: any) => {
          if (dates && dates.length === 2) onChange(moment(dates[0]).format(F), moment(dates[1]).format(F));
          else fp.setDate(valor, false);
        }}
      />
    </div>
  );
};

/* ---------- Tabla ---------- */
// columns: [{ key, label, width?, sortable?, sortKey?, render?(row) }]
// sort: { key, dir: 'ASC' | 'DESC' }
export const DataTable = ({
  columns,
  rows = [],
  rowKey = 'id',
  total = 0,
  page = 1,
  pageSize = 50,
  pageSizes = [25, 50, 100],
  onPage,
  onPageSize,
  sort,
  onSort,
  onRowClick,
  loading,
  empty,
}: any) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const desde = total ? (page - 1) * pageSize + 1 : 0;
  const hasta = Math.min(total, page * pageSize);
  const primeraCarga = loading && !rows.length;

  const ordenar = (c: any) => {
    if (!c.sortable || !onSort) return;
    const key = c.sortKey || c.key;
    onSort({ key, dir: sort && sort.key === key && sort.dir === 'DESC' ? 'ASC' : 'DESC' });
  };

  return (
    <div className={cx('ihub-table-card', loading && 'is-loading')}>
      {loading ? <div className="ihub-loadbar" /> : null}
      <div className="ihub-table-wrap">
        <table className="ihub-table">
          <thead>
            <tr>
              {columns.map((c: any) => {
                const key = c.sortKey || c.key;
                const on = sort && sort.key === key;
                return (
                  <th
                    key={c.key}
                    style={c.width ? { width: c.width } : undefined}
                    className={cx(c.sortable && 'is-sortable', on && 'is-sorted')}
                    onClick={() => ordenar(c)}
                  >
                    {c.label}
                    {c.sortable ? (
                      <i className={cx('bx ihub-sort', on ? (sort.dir === 'ASC' ? 'bx-chevron-up' : 'bx-chevron-down') : 'bx-sort-alt-2')} />
                    ) : null}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {primeraCarga ? (
              Array.from({ length: 8 }).map((_x, i) => (
                <tr key={`sk-${i}`}>
                  {columns.map((c: any, j: number) => (
                    <td key={c.key}>
                      <div className="ihub-skel" style={{ width: `${45 + ((i * 37 + j * 23) % 50)}%` }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length ? (
              rows.map((r: any) => (
                <tr
                  key={r[rowKey]}
                  className={cx(onRowClick && 'is-clickable')}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={() => onRowClick && onRowClick(r)}
                  onKeyDown={(e: any) => onRowClick && e.key === 'Enter' && onRowClick(r)}
                >
                  {columns.map((c: any) => (
                    <td key={c.key}>{c.render ? c.render(r) : r[c.key]}</td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="ihub-td-empty">
                  {empty || <EmptyState />}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="ihub-table-foot">
        <span>
          {total ? (
            <>
              Showing <b>{desde.toLocaleString()}</b> to <b>{hasta.toLocaleString()}</b> of <b>{total.toLocaleString()}</b>
            </>
          ) : (
            'No results'
          )}
        </span>
        <div className="ihub-pager">
          {onPageSize ? (
            <Segmented size="sm" options={pageSizes.map((n: number) => ({ value: n, label: String(n) }))} value={pageSize} onChange={onPageSize} />
          ) : null}
          <Button variant="secondary" size="sm" icon="bx-chevron-left" aria-label="Previous page" disabled={page <= 1} onClick={() => onPage && onPage(page - 1)} />
          <span className="ihub-pager__n">
            Page {page} of {pages}
          </span>
          <Button variant="secondary" size="sm" icon="bx-chevron-right" aria-label="Next page" disabled={page >= pages} onClick={() => onPage && onPage(page + 1)} />
        </div>
      </div>
    </div>
  );
};

/* ---------- Drawer lateral (reemplaza los popups para ver detalle) ---------- */
export const Drawer = ({ open, onClose, eyebrow, title, badges, actions, children }: any) => {
  useEffect(() => {
    if (!open) return undefined;
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', tecla);
    const antes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', tecla);
      document.body.style.overflow = antes;
    };
  }, [open, onClose]);

  if (!open) return null;
  return ReactDOM.createPortal(
    <div className="ihub-portal">
      <div className="ihub-overlay" onClick={onClose} />
      <aside className="ihub-drawer" role="dialog" aria-modal="true">
        <header className="ihub-drawer__head">
          <div className="ihub-drawer__titles">
            {eyebrow ? <div className="ihub-eyebrow">{eyebrow}</div> : null}
            <h2 className="ihub-drawer__title">{title}</h2>
            {badges ? <div className="ihub-drawer__badges">{badges}</div> : null}
          </div>
          <div className="ihub-actions">
            {actions}
            <Button variant="ghost" icon="bx-x" aria-label="Close" onClick={onClose} />
          </div>
        </header>
        <div className="ihub-drawer__body">{children}</div>
      </aside>
    </div>,
    document.body,
  );
};

// items: [{ label, value, wide? }]
export const MetaGrid = ({ items }: any) => (
  <dl className="ihub-meta">
    {items.map((it: any) => (
      <div key={it.label} className={cx('ihub-meta__item', it.wide && 'is-wide')}>
        <dt>{it.label}</dt>
        <dd>{it.value}</dd>
      </div>
    ))}
  </dl>
);
